<template>
  <div class="container-fluid container-wrap col-sm-12 col-xs-12">
    <div class="row justify-content-center">
      <div class="col-sm-12 col-xs-12">
        <div class="pb-0 col-sm-12 d-flex mainDiv">
          <div class="col-sm-12 col-xs-12">
            <!-- 페이지 헤더 -->
            <div class="page-header">
              <div class="header-left">
                <h1 class="page-title">
                  <i class="fa-solid fa-clock-rotate-left" />
                  결제내역
                </h1>
                <p class="page-description">
                  전체 결제 내역을 조회하고 관리할 수 있습니다.
                </p>
              </div>
            </div>

            <!-- 통계 카드 -->
            <div class="stats-cards">
              <div class="stat-card total">
                <div class="stat-icon">
                  <i class="fa-solid fa-won-sign" />
                </div>
                <div class="stat-content">
                  <div class="stat-label">총 결제금액</div>
                  <div class="stat-value">
                    {{ formatPrice(stats.totalAmount) }}
                  </div>
                </div>
              </div>
              <div class="stat-card count">
                <div class="stat-icon">
                  <i class="fa-solid fa-file-invoice" />
                </div>
                <div class="stat-content">
                  <div class="stat-label">총 결제건수</div>
                  <div class="stat-value">
                    {{ stats.totalCount.toLocaleString() }}건
                  </div>
                </div>
              </div>
              <div class="stat-card success">
                <div class="stat-icon">
                  <i class="fa-solid fa-circle-check" />
                </div>
                <div class="stat-content">
                  <div class="stat-label">결제 성공</div>
                  <div class="stat-value">
                    {{ stats.completedCount.toLocaleString() }}건
                  </div>
                </div>
              </div>
              <div class="stat-card failed">
                <div class="stat-icon">
                  <i class="fa-solid fa-circle-xmark" />
                </div>
                <div class="stat-content">
                  <div class="stat-label">결제 실패</div>
                  <div class="stat-value">
                    {{ stats.failedCount.toLocaleString() }}건
                  </div>
                </div>
              </div>
            </div>

            <!-- 검색/필터 및 테이블 -->
            <div class="content-card">
              <!-- 검색 영역 -->
              <div class="filter-section">
                <div class="filter-row">
                  <a-select
                    v-model:value="searchForm.resellerId"
                    :options="resellerOptions"
                    show-search
                    option-filter-prop="label"
                    placeholder="리셀러"
                    style="width: 200px"
                    @change="handleSearch"
                  />
                  <a-input-search
                    v-model:value="searchForm.companyName"
                    placeholder="회사명으로 검색"
                    style="width: 200px"
                    allow-clear
                    @search="handleSearch"
                  />
                  <a-input-search
                    v-model:value="searchForm.packageName"
                    placeholder="상품명으로 검색"
                    style="width: 180px"
                    allow-clear
                    @search="handleSearch"
                  />
                  <a-select
                    v-model:value="searchForm.status"
                    placeholder="결제상태"
                    allow-clear
                    style="width: 120px"
                    @change="handleStatusChange"
                  >
                    <a-select-option value="COMPLETED">완료</a-select-option>
                    <a-select-option value="PENDING">대기중</a-select-option>
                    <a-select-option value="FAILED">실패</a-select-option>
                    <a-select-option value="REFUNDED">환불</a-select-option>
                    <a-select-option value="PARTIAL_REFUNDED"
                      >부분환불</a-select-option
                    >
                    <a-select-option value="CANCELED">취소</a-select-option>
                  </a-select>
                </div>
                <div class="filter-row">
                  <a-radio-group
                    v-model:value="searchForm.dateType"
                    button-style="solid"
                    size="small"
                    @change="handleDateTypeChange"
                  >
                    <a-radio-button value="paidAt">결제일 기준</a-radio-button>
                    <a-radio-button value="createdAt">등록일 기준</a-radio-button>
                  </a-radio-group>
                  <div class="month-filter-group">
                    <span class="filter-label">기간:</span>
                    <a-button
                      v-for="month in recentMonths"
                      :key="month.value"
                      :type="
                        selectedMonth === month.value ? 'primary' : 'default'
                      "
                      size="small"
                      @click="handleMonthFilter(month.value)"
                    >
                      {{ month.label }}
                    </a-button>
                  </div>
                  <a-range-picker
                    v-model:value="searchForm.dateRange"
                    style="width: 260px"
                    @change="handleDateRangeChange"
                  />
                  <a-button
                    :loading="excelLoading"
                    class="excel-button"
                    @click="handleExcelDownload"
                  >
                    <i class="fa-solid fa-file-excel" /> 엑셀 다운로드
                  </a-button>
                </div>
                <div v-if="showPaidAtNotice" class="date-type-notice">
                  결제일이 없는 실패·대기 건은 제외됩니다. 포함하려면 등록일
                  기준을 선택하세요.
                </div>
              </div>

              <!-- 테이블 -->
              <a-table
                :columns="columns"
                :data-source="paymentHistoryList"
                :loading="isLoading"
                :pagination="pagination"
                :row-key="(record) => record.id || record._id"
                @change="handleTableChange"
                :scroll="{ x: 1500 }"
                class="payment-table"
              >
                <template #bodyCell="{ column, record }">
                  <!-- 회사명 -->
                  <template v-if="column.key === 'companyName'">
                    <div class="company-cell">
                      <span class="company-name">{{
                        record.companyName || "-"
                      }}</span>
                      <span class="package-name">{{
                        record.productName || "-"
                      }}</span>
                    </div>
                  </template>
                  <!-- 리셀러 -->
                  <template v-else-if="column.key === 'resellerName'">
                    <span>{{ record.resellerName || "-" }}</span>
                  </template>
                  <!-- 구분 (회차/설치비) -->
                  <template v-else-if="column.key === 'paymentRound'">
                    <template
                      v-if="
                        record.paymentType === 'INSTALLATION_FEE' ||
                        record.paymentRound === 0
                      "
                    >
                      <a-tag color="purple">설치비</a-tag>
                    </template>
                    <template v-else>
                      <span class="round-number"
                        >{{ record.paymentRound }}회차</span
                      >
                    </template>
                  </template>
                  <!-- 청구기간 -->
                  <template v-else-if="column.key === 'billingPeriod'">
                    <div class="billing-cell">
                      <span v-if="record.notes" class="billing-period">
                        {{ extractBillingPeriod(record.notes) }}
                      </span>
                      <span v-else class="text-muted">-</span>
                      <a-tag
                        v-if="record.notes && record.notes.includes('일할계산')"
                        color="blue"
                        size="small"
                      >
                        일할계산
                      </a-tag>
                    </div>
                  </template>
                  <!-- 결제금액 -->
                  <template v-else-if="column.key === 'amount'">
                    <div class="amount-cell">
                      <!-- 전액 취소: 빨간색 취소선 -->
                      <template
                        v-if="
                          record.status === 'REFUNDED' ||
                          record.status === 'CANCELED'
                        "
                      >
                        <span class="payment-amount canceled-full">
                          {{
                            formatPrice(record.originalAmount || record.amount)
                          }}
                        </span>
                      </template>
                      <!-- 부분 취소: 원금액 취소선 + 남은 금액 -->
                      <template
                        v-else-if="record.status === 'PARTIAL_REFUNDED'"
                      >
                        <span class="payment-amount canceled-partial">
                          {{
                            formatPrice(record.originalAmount || record.amount)
                          }}
                        </span>
                        <span class="payment-amount remaining">
                          {{ formatPrice(record.amount) }}
                        </span>
                      </template>
                      <!-- 정상 -->
                      <template v-else>
                        <span class="payment-amount">
                          {{ formatPrice(record.amount) }}
                        </span>
                      </template>
                    </div>
                  </template>
                  <!-- 결제카드 -->
                  <template v-else-if="column.key === 'cardNumber'">
                    <div class="card-cell">
                      <span class="card-number">{{
                        record.cardNumber || "-"
                      }}</span>
                      <span v-if="record.cardCompany" class="card-company">
                        {{ record.cardCompany }}
                      </span>
                    </div>
                  </template>
                  <!-- 결제일시 -->
                  <template v-else-if="column.key === 'paidAt'">
                    <span>{{ formatDateTime(record.paidAt) || "-" }}</span>
                  </template>
                  <!-- 등록일시 -->
                  <template v-else-if="column.key === 'createdAt'">
                    <span>{{ formatDateTime(record.createdAt) }}</span>
                  </template>
                  <!-- 상태 -->
                  <template v-else-if="column.key === 'status'">
                    <a-tag :color="PaymentStatusColor[record.status]">
                      {{ PaymentStatusLabel[record.status] }}
                    </a-tag>
                  </template>
                  <!-- 실패사유 -->
                  <template v-else-if="column.key === 'failReason'">
                    <a-tooltip
                      v-if="record.status === 'FAILED' && record.failReason"
                      :title="record.failReason"
                    >
                      <span class="fail-reason">
                        {{ truncateText(record.failReason, 20) }}
                      </span>
                    </a-tooltip>
                    <span v-else class="text-muted">-</span>
                  </template>
                  <!-- 영수증 -->
                  <template v-else-if="column.key === 'receiptUrl'">
                    <a
                      v-if="record.receiptUrl"
                      :href="record.receiptUrl"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i class="fa-solid fa-receipt" /> 영수증
                    </a>
                    <span v-else class="text-muted">-</span>
                  </template>
                  <!-- 관리 -->
                  <template v-else-if="column.key === 'action'">
                    <a-button
                      v-if="
                        record.status === 'COMPLETED' ||
                        record.status === 'PARTIAL_REFUNDED'
                      "
                      type="link"
                      danger
                      size="small"
                      @click="openCancelModal(record)"
                    >
                      취소
                    </a-button>
                    <span v-else class="text-muted">-</span>
                  </template>
                </template>
              </a-table>
            </div>

            <!-- 결제 취소 모달 -->
            <a-modal
              v-model:open="cancelModalVisible"
              title="결제 취소"
              :confirm-loading="cancelLoading"
              @ok="handleCancelPayment"
              @cancel="closeCancelModal"
              ok-text="취소 진행"
              cancel-text="닫기"
              ok-type="danger"
            >
              <div v-if="cancelTarget" class="cancel-modal-content">
                <div class="cancel-info-card">
                  <div class="info-row">
                    <span class="label">회사명</span>
                    <span class="value">{{ cancelTarget.companyName }}</span>
                  </div>
                  <div class="info-row">
                    <span class="label">상품명</span>
                    <span class="value">{{ cancelTarget.productName }}</span>
                  </div>
                  <div class="info-row">
                    <span class="label">결제금액</span>
                    <span class="value amount">{{
                      formatPrice(cancelTarget.amount)
                    }}</span>
                  </div>
                  <div v-if="cancelTarget.canceledAmount" class="info-row">
                    <span class="label">기취소금액</span>
                    <span class="value canceled">{{
                      formatPrice(cancelTarget.canceledAmount)
                    }}</span>
                  </div>
                  <div class="info-row highlight">
                    <span class="label">취소가능금액</span>
                    <span class="value">{{
                      formatPrice(maxCancelAmount)
                    }}</span>
                  </div>
                </div>

                <a-divider />

                <a-form layout="vertical">
                  <a-form-item label="취소 유형" required>
                    <a-radio-group v-model:value="cancelForm.cancelType">
                      <a-radio value="full">전액 취소</a-radio>
                      <a-radio value="partial">부분 취소</a-radio>
                    </a-radio-group>
                  </a-form-item>
                  <a-form-item
                    v-if="cancelForm.cancelType === 'partial'"
                    label="취소 금액"
                    required
                  >
                    <a-input-number
                      v-model:value="cancelForm.cancelAmount"
                      :min="1"
                      :max="maxCancelAmount"
                      :formatter="
                        (value) =>
                          `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      "
                      :parser="(value) => Number(value.replace(/,/g, ''))"
                      style="width: 200px"
                    />
                    <span class="unit">원</span>
                  </a-form-item>
                  <a-form-item label="취소 사유" required>
                    <a-select
                      v-model:value="cancelForm.cancelReasonType"
                      placeholder="취소 사유를 선택하세요"
                      style="width: 100%"
                      @change="handleCancelReasonTypeChange"
                    >
                      <a-select-option value="고객 요청"
                        >고객 요청</a-select-option
                      >
                      <a-select-option value="단순 변심"
                        >단순 변심</a-select-option
                      >
                      <a-select-option value="서비스 불만족"
                        >서비스 불만족</a-select-option
                      >
                      <a-select-option value="중복 결제"
                        >중복 결제</a-select-option
                      >
                      <a-select-option value="오결제">오결제</a-select-option>
                      <a-select-option value="계약 해지"
                        >계약 해지</a-select-option
                      >
                      <a-select-option value="기타"
                        >기타 (직접입력)</a-select-option
                      >
                    </a-select>
                  </a-form-item>
                  <a-form-item
                    v-if="cancelForm.cancelReasonType === '기타'"
                    label="상세 사유"
                    required
                  >
                    <a-input
                      v-model:value="cancelForm.cancelReasonCustom"
                      placeholder="취소 사유를 직접 입력하세요"
                    />
                  </a-form-item>
                </a-form>
              </div>
            </a-modal>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, reactive, onMounted, computed } from "vue";
import { message, Modal } from "ant-design-vue";
import dayjs from "dayjs";
import { resellerPaymentHistoryApi } from "./api";
import { formatPrice, formatDateTime } from "./utils/formatters";
import { PaymentStatusLabel, PaymentStatusColor } from "./enums";
import {
  PAYMENT_HISTORY_EXPORT_MAX,
  buildPaymentHistoryListParams,
  buildPaymentHistoryStatsParams,
  nextSortOnDateTypeChange,
  resolveSort,
} from "./utils/paymentHistoryQuery";
import {
  buildPaymentHistoryFilename,
  buildPaymentHistorySheetRows,
  writePaymentHistoryWorkbook,
} from "./utils/paymentHistoryExcel";

export default defineComponent({
  name: "PaymentHistoryV2",
  setup() {
    const isLoading = ref(false);
    const paymentHistoryList = ref([]);

    // 통계
    const stats = ref({
      totalCount: 0,
      totalAmount: 0,
      completedCount: 0,
      failedCount: 0,
    });

    const searchForm = reactive({
      resellerId: "", // "" = 전체
      companyName: "",
      packageName: "",
      status: undefined,
      dateType: "paidAt", // 기간 기준: 결제일(기본) / 등록일
      dateRange: null,
    });

    // 정렬 상태 (기본: 기간 기준 필드 내림차순)
    const sort = ref({ sortBy: "paidAt", sortOrder: "desc" });

    // 리셀러 선택 목록 ("전체" + 삭제되지 않은 RESELLER 회사)
    const resellerOptions = ref([{ value: "", label: "전체" }]);

    const excelLoading = ref(false);

    // 폼 → 요청 조건용 값 (기간은 YYYY-MM-DD 문자열)
    const getCurrentForm = () => {
      const range = searchForm.dateRange;
      const hasRange = range && range[0] && range[1];
      return {
        resellerId: searchForm.resellerId,
        companyName: searchForm.companyName,
        packageName: searchForm.packageName,
        status: searchForm.status,
        dateType: searchForm.dateType,
        startDate: hasRange ? range[0].format("YYYY-MM-DD") : undefined,
        endDate: hasRange ? range[1].format("YYYY-MM-DD") : undefined,
      };
    };

    // 결제일 기준 + 기간 지정 시에만 안내 표시
    const showPaidAtNotice = computed(
      () =>
        searchForm.dateType === "paidAt" &&
        !!(searchForm.dateRange && searchForm.dateRange[0] && searchForm.dateRange[1])
    );

    // 선택된 월 필터 (YYYY-MM 형식)
    const selectedMonth = ref(null);

    // 최근 3개월 버튼 목록
    const recentMonths = computed(() => {
      const today = dayjs();
      const months = [];

      for (let i = 0; i < 3; i++) {
        const targetMonth = today.subtract(i, "month");
        months.push({
          label: `${targetMonth.month() + 1}월`,
          value: targetMonth.format("YYYY-MM"),
        });
      }

      return months;
    });

    // 월 필터 버튼 클릭 핸들러
    const handleMonthFilter = (monthValue) => {
      if (selectedMonth.value === monthValue) {
        selectedMonth.value = null;
        searchForm.dateRange = null;
      } else {
        selectedMonth.value = monthValue;
        const startOfMonth = dayjs(monthValue + "-01").startOf("month");
        const endOfMonth = dayjs(monthValue + "-01").endOf("month");
        searchForm.dateRange = [startOfMonth, endOfMonth];
      }
      handleSearch();
    };

    // 날짜 범위 직접 선택 시 월 필터 해제
    const handleDateRangeChange = () => {
      selectedMonth.value = null;
      handleSearch();
    };

    const pagination = reactive({
      current: 1,
      pageSize: 20,
      total: 0,
      showSizeChanger: true,
      showTotal: (total) => `총 ${total}개`,
    });

    // 정렬 상태 → antd sortOrder
    const sortOrderOf = (key) => {
      if (sort.value.sortBy !== key) return null;
      return sort.value.sortOrder === "asc" ? "ascend" : "descend";
    };

    // 테이블 컬럼 정의 (정렬 열은 sort 상태로 제어)
    const columns = computed(() => [
      {
        title: "거래처",
        key: "companyName",
        dataIndex: "companyName",
        width: 200,
        fixed: "left",
      },
      {
        title: "리셀러",
        key: "resellerName",
        dataIndex: "resellerName",
        width: 140,
      },
      {
        title: "구분",
        key: "paymentRound",
        dataIndex: "paymentRound",
        width: 90,
        align: "center",
      },
      {
        title: "청구기간",
        key: "billingPeriod",
        dataIndex: "notes",
        width: 180,
      },
      {
        title: "결제금액",
        key: "amount",
        dataIndex: "amount",
        width: 140,
        align: "right",
        sorter: true,
        sortOrder: sortOrderOf("amount"),
      },
      {
        title: "결제카드",
        key: "cardNumber",
        dataIndex: "cardNumber",
        width: 180,
      },
      {
        title: "결제일시",
        key: "paidAt",
        dataIndex: "paidAt",
        width: 150,
        align: "center",
        sorter: true,
        sortDirections: ["descend", "ascend"],
        sortOrder: sortOrderOf("paidAt"),
      },
      {
        title: "등록일시",
        key: "createdAt",
        dataIndex: "createdAt",
        width: 150,
        align: "center",
        sorter: true,
        sortDirections: ["descend", "ascend"],
        sortOrder: sortOrderOf("createdAt"),
      },
      {
        title: "상태",
        key: "status",
        dataIndex: "status",
        width: 90,
        align: "center",
      },
      {
        title: "실패사유",
        key: "failReason",
        dataIndex: "failReason",
        width: 150,
      },
      {
        title: "영수증",
        key: "receiptUrl",
        dataIndex: "receiptUrl",
        width: 90,
        align: "center",
      },
      {
        title: "관리",
        key: "action",
        width: 80,
        align: "center",
        fixed: "right",
      },
    ]);

    // 청구기간 추출 헬퍼
    const extractBillingPeriod = (notes) => {
      if (!notes) return "";
      const match = notes.match(/\d{4}-\d{2}-\d{2}\s*~\s*\d{4}-\d{2}-\d{2}/);
      return match ? match[0] : notes;
    };

    // 텍스트 자르기 헬퍼
    const truncateText = (text, maxLength) => {
      if (!text) return "";
      return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
    };

    // 통계 조회
    const fetchStats = async () => {
      try {
        const response = await resellerPaymentHistoryApi.getStats(
          buildPaymentHistoryStatsParams(getCurrentForm())
        );
        const data = response.data || response;
        const completedStats = data.completed || { count: 0, totalAmount: 0 };
        const failedStats = data.failed || { count: 0, totalAmount: 0 };
        const pendingStats = data.pending || { count: 0, totalAmount: 0 };
        const refundedStats = data.refunded || { count: 0, totalAmount: 0 };
        const canceledStats = data.canceled || { count: 0, totalAmount: 0 };

        stats.value = {
          totalCount:
            completedStats.count +
            failedStats.count +
            pendingStats.count +
            refundedStats.count +
            canceledStats.count,
          totalAmount: completedStats.totalAmount,
          completedCount: completedStats.count,
          failedCount: failedStats.count,
        };
      } catch (error) {
        console.error("통계 조회 실패:", error);
      }
    };

    const fetchList = async () => {
      try {
        isLoading.value = true;

        const params = buildPaymentHistoryListParams(getCurrentForm(), {
          page: pagination.current,
          limit: pagination.pageSize,
          sort: sort.value,
        });

        const response = await resellerPaymentHistoryApi.getList(params);
        const data = response.data || response;
        paymentHistoryList.value = data.docs || data.data || [];
        pagination.total = data.totalDocs || data.total || 0;
      } catch (error) {
        console.error("결제내역 목록 조회 실패:", error);
        message.error("결제내역을 불러오는데 실패했습니다.");
        paymentHistoryList.value = [];
        pagination.total = 0;
      } finally {
        isLoading.value = false;
      }
    };

    // 조건(리셀러·기간 기준·기간·회사명·상품명) 변경: 1페이지 목록 + 통계
    const handleSearch = () => {
      pagination.current = 1;
      fetchList();
      fetchStats();
    };

    // 상태 선택: 통계는 상태를 따르지 않으므로 목록만 1페이지로 다시 조회
    const handleStatusChange = () => {
      pagination.current = 1;
      fetchList();
    };

    // 기간 기준 변경: 정렬을 새 기준에 맞추고(금액 정렬은 유지) 다시 조회
    const handleDateTypeChange = () => {
      sort.value = nextSortOnDateTypeChange(sort.value, searchForm.dateType);
      handleSearch();
    };

    // 열 정렬 변경·해제는 1페이지부터, 페이지 이동은 해당 페이지만 목록 재조회
    const handleTableChange = (pag, filters, sorter, extra) => {
      if (extra && extra.action === "sort") {
        sort.value = resolveSort(sorter, searchForm.dateType);
        pagination.current = 1;
      } else {
        pagination.current = pag.current || 1;
      }
      pagination.pageSize = pag.pageSize || 20;
      fetchList();
    };

    // 리셀러 선택 목록 조회
    const fetchResellerOptions = async () => {
      try {
        const options = await resellerPaymentHistoryApi.getResellerOptions();
        resellerOptions.value = [{ value: "", label: "전체" }, ...options];
      } catch (error) {
        console.error("리셀러 목록 조회 실패:", error);
        message.error("리셀러 목록을 불러오는데 실패했습니다.");
      }
    };

    // 엑셀 다운로드: 현재 조건·정렬로 전체 조회(현재 페이지와 무관), 상한 초과 시 저장하지 않음
    const handleExcelDownload = async () => {
      if (excelLoading.value) return;
      try {
        excelLoading.value = true;
        const form = getCurrentForm();
        const params = buildPaymentHistoryListParams(form, {
          page: 1,
          limit: PAYMENT_HISTORY_EXPORT_MAX,
          sort: sort.value,
        });
        const response = await resellerPaymentHistoryApi.getList(params);
        const data = response.data || response;
        const docs = data.docs || [];
        const total = data.totalDocs || 0;

        if (total > PAYMENT_HISTORY_EXPORT_MAX) {
          message.warning(
            `조건에 맞는 결제가 ${total.toLocaleString()}건으로 최대 ${PAYMENT_HISTORY_EXPORT_MAX.toLocaleString()}건을 넘습니다. 기간이나 리셀러를 좁혀 주세요.`
          );
          return;
        }
        if (total === 0) {
          message.info("다운로드할 데이터가 없습니다.");
          return;
        }
        if (docs.length < total) {
          // 일부만 담긴 파일은 만들지 않는다
          throw new Error(`응답 건수 불일치: ${docs.length}/${total}`);
        }

        const resellerName =
          resellerOptions.value.find((o) => o.value === form.resellerId)
            ?.label || "";
        writePaymentHistoryWorkbook(
          buildPaymentHistorySheetRows(docs),
          buildPaymentHistoryFilename({
            resellerName: form.resellerId ? resellerName : "",
            dateType: form.dateType,
            startDate: form.startDate,
            endDate: form.endDate,
          })
        );
        message.success(`${total.toLocaleString()}건을 엑셀로 저장했습니다.`);
      } catch (error) {
        console.error("결제내역 엑셀 다운로드 실패:", error);
        message.error("엑셀 다운로드에 실패했습니다. 잠시 후 다시 시도해 주세요.");
      } finally {
        excelLoading.value = false;
      }
    };

    // ===== 결제 취소 관련 =====
    const cancelModalVisible = ref(false);
    const cancelLoading = ref(false);
    const cancelTarget = ref(null);
    const cancelForm = reactive({
      cancelType: "full",
      cancelAmount: 0,
      cancelReasonType: "",
      cancelReasonCustom: "",
    });

    // 취소 가능 금액 계산
    const maxCancelAmount = computed(() => {
      if (!cancelTarget.value) return 0;
      return (
        cancelTarget.value.amount - (cancelTarget.value.canceledAmount || 0)
      );
    });

    // 취소 모달 열기
    const openCancelModal = (record) => {
      cancelTarget.value = record;
      cancelForm.cancelType = "full";
      cancelForm.cancelAmount = record.amount - (record.canceledAmount || 0);
      cancelForm.cancelReasonType = "";
      cancelForm.cancelReasonCustom = "";
      cancelModalVisible.value = true;
    };

    // 취소 사유 타입 변경 핸들러
    const handleCancelReasonTypeChange = () => {
      if (cancelForm.cancelReasonType !== "기타") {
        cancelForm.cancelReasonCustom = "";
      }
    };

    // 취소 모달 닫기
    const closeCancelModal = () => {
      cancelModalVisible.value = false;
      cancelTarget.value = null;
    };

    // 결제 취소 처리
    const handleCancelPayment = async () => {
      if (!cancelTarget.value) return;

      if (!cancelForm.cancelReasonType) {
        message.warning("취소 사유를 선택해주세요.");
        return;
      }

      if (
        cancelForm.cancelReasonType === "기타" &&
        !cancelForm.cancelReasonCustom.trim()
      ) {
        message.warning("취소 사유를 직접 입력해주세요.");
        return;
      }

      if (cancelForm.cancelType === "partial") {
        if (!cancelForm.cancelAmount || cancelForm.cancelAmount <= 0) {
          message.warning("취소 금액을 입력해주세요.");
          return;
        }
        if (cancelForm.cancelAmount > maxCancelAmount.value) {
          message.warning("취소 가능 금액을 초과했습니다.");
          return;
        }
      }

      const cancelAmount =
        cancelForm.cancelType === "full" ? undefined : cancelForm.cancelAmount;

      const cancelReason =
        cancelForm.cancelReasonType === "기타"
          ? cancelForm.cancelReasonCustom.trim()
          : cancelForm.cancelReasonType;

      Modal.confirm({
        title: "결제 취소 확인",
        content: `${
          cancelForm.cancelType === "full"
            ? "전액"
            : formatPrice(cancelForm.cancelAmount)
        }을(를) 취소하시겠습니까?`,
        okText: "취소 진행",
        okType: "danger",
        cancelText: "닫기",
        async onOk() {
          try {
            cancelLoading.value = true;
            await resellerPaymentHistoryApi.cancelPayment(
              cancelTarget.value._id,
              cancelReason,
              cancelAmount
            );

            message.success("결제가 취소되었습니다.");
            closeCancelModal();
            fetchList();
            fetchStats();
          } catch (error) {
            console.error("결제 취소 실패:", error);
            const errorMessage =
              error.response?.data?.message || "결제 취소에 실패했습니다.";
            message.error(errorMessage);
          } finally {
            cancelLoading.value = false;
          }
        },
      });
    };

    onMounted(() => {
      fetchResellerOptions();
      fetchStats();
      fetchList();
    });

    return {
      isLoading,
      paymentHistoryList,
      stats,
      searchForm,
      resellerOptions,
      showPaidAtNotice,
      excelLoading,
      pagination,
      columns,
      selectedMonth,
      recentMonths,
      handleMonthFilter,
      handleDateRangeChange,
      handleSearch,
      handleStatusChange,
      handleDateTypeChange,
      handleExcelDownload,
      handleTableChange,
      formatPrice,
      formatDateTime,
      extractBillingPeriod,
      truncateText,
      PaymentStatusLabel,
      PaymentStatusColor,
      // 취소 관련
      cancelModalVisible,
      cancelLoading,
      cancelTarget,
      cancelForm,
      maxCancelAmount,
      openCancelModal,
      closeCancelModal,
      handleCancelPayment,
      handleCancelReasonTypeChange,
    };
  },
});
</script>

<style lang="scss" scoped>
.row {
  padding: 28px 28px 72px 28px !important;
}

/* ============================================ */
/* 페이지 헤더 */
/* ============================================ */
.date-type-notice {
  margin-top: 8px;
  font-size: 12px;
  color: #8c8c8c;
}

.excel-button {
  margin-left: auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
}

.header-left {
  flex: 1;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #1a1a2e;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.page-description {
  font-size: 14px;
  color: #666;
  margin: 0;
}

/* ============================================ */
/* 통계 카드 */
/* ============================================ */
.stats-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

@media (max-width: 1024px) {
  .stats-cards {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 640px) {
  .stats-cards {
    grid-template-columns: 1fr;
  }
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  gap: 16px;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.stat-card.total .stat-icon {
  background: #e6f7ff;
  color: #1890ff;
}

.stat-card.count .stat-icon {
  background: #f9f0ff;
  color: #722ed1;
}

.stat-card.success .stat-icon {
  background: #f6ffed;
  color: #52c41a;
}

.stat-card.failed .stat-icon {
  background: #fff2f0;
  color: #ff4d4f;
}

.stat-content {
  flex: 1;
}

.stat-label {
  font-size: 13px;
  color: #666;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 22px;
  font-weight: 700;
  color: #1a1a2e;
}

/* ============================================ */
/* 콘텐츠 카드 */
/* ============================================ */
.content-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 24px;
}

/* ============================================ */
/* 필터 섹션 */
/* ============================================ */
.filter-section {
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.filter-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
}

.filter-label {
  font-size: 13px;
  color: #666;
  margin-right: 4px;
}

.month-filter-group {
  display: flex;
  gap: 6px;
  align-items: center;
}

.month-filter-group :deep(.ant-btn) {
  min-width: 50px;
}

/* ============================================ */
/* 테이블 */
/* ============================================ */
.payment-table :deep(.ant-table-thead > tr > th) {
  background: #fafafa;
  font-weight: 600;
  white-space: nowrap;
}

.payment-table :deep(.ant-table-tbody > tr > td) {
  padding: 14px 12px;
}

/* 회사 셀 */
.company-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.company-name {
  font-weight: 500;
  color: #1a1a2e;
}

.package-name {
  font-size: 12px;
  color: #666;
}

/* 회차 */
.round-number {
  font-weight: 500;
}

/* 청구기간 셀 */
.billing-cell {
  display: flex;
  align-items: center;
  gap: 6px;
}

.billing-period {
  font-size: 13px;
  color: #666;
}

/* 금액 셀 */
.amount-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.payment-amount {
  font-weight: 600;
  color: #1a1a2e;
}

.payment-amount.canceled-full {
  color: #ff4d4f;
  text-decoration: line-through;
}

.payment-amount.canceled-partial {
  color: #1a1a2e;
  text-decoration: line-through;
  font-size: 12px;
  font-weight: 400;
}

.payment-amount.remaining {
  color: #1890ff;
  font-weight: 700;
}

/* 카드 셀 */
.card-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.card-number {
  font-family: monospace;
  font-size: 13px;
}

.card-company {
  font-size: 11px;
  color: #666;
}

/* 실패 사유 */
.fail-reason {
  color: #ff4d4f;
  font-size: 12px;
  cursor: help;
}

/* 유틸리티 */
.text-muted {
  color: #999;
}

/* ============================================ */
/* 취소 모달 */
/* ============================================ */
.cancel-modal-content {
  padding: 8px 0;
}

.cancel-info-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
}

.cancel-info-card .info-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.cancel-info-card .info-row:last-child {
  border-bottom: none;
}

.cancel-info-card .info-row .label {
  color: #666;
  font-size: 13px;
}

.cancel-info-card .info-row .value {
  font-weight: 500;
  color: #1a1a2e;
}

.cancel-info-card .info-row .value.amount {
  color: #1890ff;
}

.cancel-info-card .info-row .value.canceled {
  color: #ff4d4f;
}

.cancel-info-card .info-row.highlight {
  background: #e6f7ff;
  margin: 8px -16px -16px;
  padding: 12px 16px;
  border-radius: 0 0 8px 8px;
}

.cancel-info-card .info-row.highlight .value {
  color: #52c41a;
  font-weight: 600;
  font-size: 16px;
}

.unit {
  margin-left: 8px;
  color: #666;
}
</style>
